import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, Upload, Download, Loader, Eye, EyeOff, Layout as LayoutIcon, Home, Image as ImageIcon, FileText } from 'lucide-react';
import MediaSelector from '@/components/admin/MediaSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_DESTINATION = {
  slug: '',
  name: '',
  continent: 'Otros',
  title: '',
  description: '',
  introTitle: '',
  introText: '',
  image_url: '',
  hero_video_url: '',
  mapUrl: '',
  curiosities: [],
  events: [],
  is_active: true,
  show_on_home: false,
  order: 0,
  content_blocks: [], 
  meta_title: '',
  meta_description: '',
  custom_guide_path: null
};

export default function AdminDestinations() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDest, setEditingDest] = useState(null);
  const [formData, setFormData] = useState(EMPTY_DESTINATION);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const fileInputRef = useRef(null);
  const guideInputRef = useRef(null);
  const [uploadingGuide, setUploadingGuide] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDestinations = async () => {
    try {
      const res = await axios.get(`${API}/destinations?all_destinations=true`, { headers });
      setDestinations(res.data);
    } catch (err) {
      console.error('Error fetching destinations:', err);
      toast.error('Error al cargar destinos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDestinations();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('category', 'destinations');

    try {
      const response = await axios.post(`${API}/media/upload`, uploadData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleGuideUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (!editingDest) {
      toast.error('Guarda el destino primero antes de subir la guía');
      return;
    }

    setUploadingGuide(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/destinations/${editingDest.slug}/guide`, uploadData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, custom_guide_path: response.data.path }));
      toast.success('Guía PDF subida correctamente');
    } catch (err) {
      console.error('Error uploading guide:', err);
      toast.error('Error al subir la guía PDF');
    } finally {
      setUploadingGuide(false);
    }
  };

  const handleDownloadGuide = async (slug) => {
    try {
      const response = await axios.get(`${API}/destinations/${slug}/guide/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Guia_${slug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Descarga iniciada');
    } catch (err) {
      console.error('Error downloading guide:', err);
      toast.error('Error al descargar la guía');
    }
  };

  const openCreateDialog = () => {
    setEditingDest(null);
    setFormData(EMPTY_DESTINATION);
    setDialogOpen(true);
  };

  const openEditDialog = (dest) => {
    setEditingDest(dest);
    setFormData({
      ...EMPTY_DESTINATION,
      ...dest
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingDest) {
        await axios.put(`${API}/destinations/${editingDest.slug}`, formData, { headers });
        toast.success('Destino actualizado');
      } else {
        await axios.post(`${API}/destinations`, formData, { headers });
        toast.success('Destino creado');
      }
      setDialogOpen(false);
      fetchDestinations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (dest) => {
    try {
      await axios.put(`${API}/destinations/${dest.slug}`, { is_active: !dest.is_active }, { headers });
      fetchDestinations();
      toast.success('Estado actualizado');
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const toggleHome = async (dest) => {
    try {
      await axios.put(`${API}/destinations/${dest.slug}`, { show_on_home: !dest.show_on_home }, { headers });
      fetchDestinations();
      toast.success(dest.show_on_home ? 'Quitado de la home' : 'Añadido a la home');
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('¿Eliminar este destino definitivamente?')) return;
    try {
      await axios.delete(`${API}/destinations/${slug}`, { headers });
      fetchDestinations();
      toast.success('Destino eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-primary">
            Gestión de Destinos
          </h1>
          <p className="text-muted-foreground">
            Administra las páginas de aterrizaje por destino
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="btn-accent">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Destino
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">
                {editingDest ? 'Editar Destino' : 'Nuevo Destino'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nombre Destino (ej: Maldivas)</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Maldivas"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Continente</label>
                  <Select
                    value={formData.continent || 'Otros'}
                    onValueChange={(val) => setFormData({ ...formData, continent: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona continente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europa">Europa</SelectItem>
                      <SelectItem value="Asia">Asia</SelectItem>
                      <SelectItem value="África">África</SelectItem>
                      <SelectItem value="América">América</SelectItem>
                      <SelectItem value="Oceanía">Oceanía</SelectItem>
                      <SelectItem value="Otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">URL Slug (ej: maldivas)</label>
                  <Input
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="maldivas"
                    disabled={!!editingDest}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Orden (Prioridad)</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Título Principal (H1)</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Maldivas: Un paraíso en medio del Índico"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Descripción Intro</label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve introducción para la cabecera..."
                  rows={2}
                />
              </div>

              <div className="border p-4 rounded-lg space-y-4 bg-slate-50/50">
                <h3 className="text-sm font-semibold text-primary">Contenido Detallado (Sección 2)</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Título de Introducción Detallada</label>
                  <Input
                    value={formData.introTitle || ''}
                    onChange={(e) => setFormData({ ...formData, introTitle: e.target.value })}
                    placeholder="¿Por qué viajar a este destino con nosotros?"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Texto de Introducción Detallada</label>
                  <Textarea
                    value={formData.introText || ''}
                    onChange={(e) => setFormData({ ...formData, introText: e.target.value })}
                    placeholder="Contenido extenso explicando las ventajas..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Imagen Header</label>
                  <div className="flex gap-2">
                    <Input
                      required
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="URL de imagen"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon" 
                      title="Seleccionar de biblioteca"
                      onClick={() => setShowMediaSelector(true)}
                    >
                      <ImageIcon className="w-4 h-4" />
                    </Button>
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} className="hidden" />
                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader className="animate-spin h-4 w-4" /> : <Upload className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Video Header (Opcional URL)</label>
                  <Input
                    value={formData.hero_video_url}
                    onChange={(e) => setFormData({ ...formData, hero_video_url: e.target.value })}
                    placeholder="URL archivo .mp4"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">URL de Google Maps (Embed)</label>
                <Input
                  value={formData.mapUrl || ''}
                  onChange={(e) => setFormData({ ...formData, mapUrl: e.target.value })}
                  placeholder="https://www.google.com/maps/embed?..."
                />
              </div>

              <div className="space-y-2 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <label className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                  Guía PDF Personalizada
                  <span className="text-xs font-normal text-blue-600">(Sobrescribe la guía generada automáticamente)</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={formData.custom_guide_path || ''}
                    placeholder="Ningún archivo subido (se usará la guía automática)"
                    className="bg-white"
                  />
                  <input 
                    type="file" 
                    ref={guideInputRef} 
                    onChange={handleGuideUpload} 
                    accept=".pdf"
                    className="hidden" 
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="bg-white"
                    onClick={() => guideInputRef.current?.click()} 
                    disabled={uploadingGuide || !editingDest}
                    title={!editingDest ? "Guarda el destino primero" : "Subir PDF"}
                  >
                    {uploadingGuide ? <Loader className="animate-spin h-4 w-4" /> : <Upload className="h-4 w-4" />}
                  </Button>
                  {editingDest && (
                    <Button
                      type="button"
                      variant="outline"
                      className="bg-white"
                      onClick={() => handleDownloadGuide(editingDest.slug)}
                      title="Ver/Descargar Guía Actual"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                  {formData.custom_guide_path && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-red-500 bg-white"
                      onClick={() => setFormData({ ...formData, custom_guide_path: null })}
                      title="Eliminar guía personalizada"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {!editingDest && (
                  <p className="text-[10px] text-blue-500">Debes crear el destino antes de poder subir una guía.</p>
                )}
              </div>

              <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">Publicado / Activo</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="show_on_home"
                    checked={formData.show_on_home}
                    onChange={(e) => setFormData({ ...formData, show_on_home: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="show_on_home" className="text-sm font-medium">Mostrar en la Home</label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Orden:</label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* Curiosities */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold">Curiosidades</h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setFormData({
                      ...formData, 
                      curiosities: [...(formData.curiosities || []), { title: '', text: '' }]
                    })}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Añadir
                  </Button>
                </div>
                <div className="space-y-3">
                  {(formData.curiosities || []).map((curio, idx) => (
                    <div key={idx} className="flex gap-2 items-start bg-slate-50 p-2 rounded-lg">
                      <div className="flex-1 grid gap-2">
                        <Input 
                          placeholder="Título (ej: Silencio Sagrado)" 
                          value={curio.title}
                          onChange={(e) => {
                            const newCuriosities = [...formData.curiosities];
                            newCuriosities[idx] = { ...newCuriosities[idx], title: e.target.value };
                            setFormData({...formData, curiosities: newCuriosities});
                          }}
                        />
                        <Textarea 
                          placeholder="Texto descriptivo..."
                          value={curio.text}
                          onChange={(e) => {
                            const newCuriosities = [...formData.curiosities];
                            newCuriosities[idx] = { ...newCuriosities[idx], text: e.target.value };
                            setFormData({...formData, curiosities: newCuriosities});
                          }}
                          rows={2}
                        />
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500"
                        onClick={() => {
                          const newCuriosities = formData.curiosities.filter((_, i) => i !== idx);
                          setFormData({...formData, curiosities: newCuriosities});
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Events */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold">Eventos / Cuándo Ir</h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setFormData({
                      ...formData, 
                      events: [...(formData.events || []), { month: '', event: '', description: '' }]
                    })}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Añadir
                  </Button>
                </div>
                <div className="space-y-3">
                  {(formData.events || []).map((ev, idx) => (
                    <div key={idx} className="flex gap-2 items-start bg-slate-50 p-2 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input 
                          placeholder="Mes/Rango" 
                          value={ev.month}
                          onChange={(e) => {
                            const newEvents = [...formData.events];
                            newEvents[idx] = { ...newEvents[idx], month: e.target.value };
                            setFormData({...formData, events: newEvents});
                          }}
                        />
                        <Input 
                          placeholder="Nombre del Evento" 
                          value={ev.event}
                          onChange={(e) => {
                            const newEvents = [...formData.events];
                            newEvents[idx] = { ...newEvents[idx], event: e.target.value };
                            setFormData({...formData, events: newEvents});
                          }}
                        />
                        <Textarea 
                          placeholder="Descripción..."
                          className="col-span-2"
                          value={ev.description}
                          onChange={(e) => {
                            const newEvents = [...formData.events];
                            newEvents[idx] = { ...newEvents[idx], description: e.target.value };
                            setFormData({...formData, events: newEvents});
                          }}
                          rows={2}
                        />
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500"
                        onClick={() => {
                          const newEvents = formData.events.filter((_, i) => i !== idx);
                          setFormData({...formData, events: newEvents});
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SEO Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold mb-3">Ajustes SEO</h3>
                <div className="grid gap-4">
                  <Input
                    placeholder="Meta Title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  />
                  <Textarea
                    placeholder="Meta Description"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? 'Guardando...' : (editingDest ? 'Actualizar Destino' : 'Crear Destino')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <MediaSelector 
        open={showMediaSelector} 
        onOpenChange={setShowMediaSelector} 
        onSelect={(url) => setFormData(prev => ({ ...prev, image_url: url }))} 
      />

      {/* Destinations Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Cargando destinos...</div>
        ) : destinations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-muted-foreground w-16">Orden</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Destino</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">URL</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Home</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Estado</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {destinations.map((dest) => (
                  <tr key={dest.slug} className="border-t hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-mono text-xs">{dest.order}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img src={dest.image_url} alt={dest.name} className="w-10 h-10 rounded object-cover border" />
                        <span className="font-medium text-primary">{dest.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-500">/destinos/{dest.slug}</td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleHome(dest)}
                        className={dest.show_on_home ? "text-accent" : "text-slate-300"}
                      >
                        <Home className="h-4 w-4" fill={dest.show_on_home ? "currentColor" : "none"} />
                      </Button>
                    </td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(dest)}
                        className={`text-xs px-2 py-1 h-auto rounded-full ${
                          dest.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {dest.is_active ? <><Eye className="h-3 w-3 mr-1" /> Activo</> : <><EyeOff className="h-3 w-3 mr-1" /> Inactivo</>}
                      </Button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDownloadGuide(dest.slug)}
                          title="Descargar Guía"
                        >
                          <Download className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(dest)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(dest.slug)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 text-center text-muted-foreground">
            No has configurado destinos destacados todavía.
          </div>
        )}
      </div>
      <MediaSelector
        open={showMediaSelector}
        onOpenChange={setShowMediaSelector}
        onSelect={(url) => {
          const finalUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
          setFormData(prev => ({ ...prev, image_url: finalUrl }));
          setShowMediaSelector(false);
        }}
        type="image"
      />
    </AdminLayout>
  );
}
